import * as THREE from "three";
import { Loop } from "./core/Loop";
import { Input, SwipeDir } from "./systems/Input";
import { Storage } from "./systems/Storage";
import { Audio } from "./systems/Audio";
import { Missions } from "./systems/Missions";
import { PlayerCar } from "./game/PlayerCar";
import { Road, CHUNK_LENGTH } from "./game/Road";
import { Spawner } from "./game/Spawner";
import { intersectsSpawnable } from "./game/Collision";
import { PowerUpSystem } from "./game/PowerUps";
import { Skyline } from "./game/Skyline";
import { ArrivalScene } from "./game/ArrivalScene";
import { CARS, CarDef, carById } from "./content/cars";
import { BRAND, primaryListenLink } from "./content/brand";
import { HUD } from "./ui/HUD";
import { Intro } from "./ui/Intro";
import { Outro } from "./ui/Outro";
import { Auth } from "./ui/Auth";
import { Account } from "./systems/Account";

type GameState =
  | "intro"
  | "auth"
  | "menu"
  | "playing"
  | "paused"
  | "arrival"
  | "outro"
  | "gameover";

const LEVEL_END_DISTANCE = 2000; // metres — full LA → K-town run

const BASE_SPEED = 22; // m/s
const MAX_SPEED = 56; // m/s
const SPEED_RAMP_PER_SEC = 0.32; // additive per second of run

export class Game {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private loop: Loop;

  private player: PlayerCar;
  private road: Road;
  private spawner: Spawner;
  private skyline: Skyline;
  private powerups: PowerUpSystem;
  private storage: Storage;
  private missions: Missions;
  private audio: Audio;
  private input: Input;
  private hud: HUD;

  private intro: Intro;
  private outro: Outro;
  private auth: Auth;
  private account: Account;
  private arrival: ArrivalScene;
  private state: GameState = "intro";
  private speed = BASE_SPEED;
  private elapsed = 0;
  private distance = 0;
  private runCoins = 0;
  private runScore = 0;
  private runPowerupCount = 0;
  private runLaneChanges = 0;

  // Camera shake
  private shakeT = 0;
  private shakeAmp = 0;
  // Nitro fov interpolation
  private fovBase = 70;
  private fovTarget = 70;

  private playerBox = new THREE.Box3();

  constructor(parent: HTMLElement) {
    // ---- Renderer ----
    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.touchAction = "none";
    parent.appendChild(canvas);
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: "high-performance",
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    // ---- Scene ----
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0b0e1a);
    this.scene.fog = new THREE.Fog(0x0b0e1a, 30, 110);

    // ---- Camera ----
    this.camera = new THREE.PerspectiveCamera(this.fovBase, 1, 0.1, 300);
    this.camera.position.set(0, 4.2, 7);
    this.camera.lookAt(0, 1.2, -8);

    // ---- Lighting ----
    const hemi = new THREE.HemisphereLight(0xa0b3ff, 0x202030, 0.6);
    this.scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xffeacc, 0.8);
    sun.position.set(8, 16, 10);
    this.scene.add(sun);

    // ---- World ----
    this.player = new PlayerCar();
    this.applyCarSkin(carById("default"));
    this.road = new Road();
    this.spawner = new Spawner();
    this.skyline = new Skyline();
    this.powerups = new PowerUpSystem();
    this.arrival = new ArrivalScene(parent);
    this.scene.add(this.road.group);
    this.scene.add(this.spawner.group);
    this.scene.add(this.skyline.group);
    this.scene.add(this.arrival.group);
    this.scene.add(this.player.group);

    // ---- Systems ----
    this.storage = new Storage();
    this.missions = new Missions(this.storage);
    this.audio = new Audio();
    this.account = new Account();
    // Push every storage write to the server (debounced inside Account).
    this.storage.setSyncCallback((d) => this.account.queueSave(d));
    // Apply equipped car
    this.applyCarSkin(carById(this.storage.get().equippedSkin));

    // Populate initial chunks
    for (const c of this.road.chunks) this.spawner.populate(c, 0);
    this.road.setRecycleHandler((chunk) => {
      // First 50m of every run is car-free so the player can settle in.
      this.spawner.populate(chunk, this.difficulty(), this.distance >= 50);
    });

    // ---- Input ----
    this.input = new Input(canvas, {
      onSwipe: (d) => this.handleSwipe(d),
      onTap: () => this.handleTap(),
      onFirstInteract: () => this.audio.unlock(),
    });
    this.input.attach();

    // ---- Intro / Outro cutscenes ----
    this.intro = new Intro(parent);
    this.outro = new Outro(parent);
    this.auth = new Auth(parent);

    // ---- HUD ----
    this.hud = new HUD(parent, this.storage);
    this.hud.refreshMissionsCb = () => this.missions.ensureFresh();
    this.hud.missionLabelCb = (m) => this.missions.labelFor(m);
    this.hud.onPlay = () => this.startRun();
    this.hud.onPause = () => this.pause();
    this.hud.onResume = () => this.resume();
    this.hud.onRestart = () => this.startRun();
    this.hud.onMenu = () => this.toMenu();
    this.hud.onMute = (m) => this.audio.setMuted(m);
    this.hud.onShare = () => this.share();
    this.hud.onSelectCar = (car) => {
      this.storage.update((d) => {
        d.equippedSkin = car.id;
      });
      this.applyCarSkin(car);
    };
    this.hud.onBuyCar = (car) => {
      const d = this.storage.get();
      if (d.ownedSkins.includes(car.id)) return false;
      if (d.totalCoins < car.price) return false;
      this.storage.update((s) => {
        s.totalCoins -= car.price;
        s.ownedSkins.push(car.id);
        s.equippedSkin = car.id;
      });
      this.applyCarSkin(car);
      return true;
    };
    this.hud.onSignOut = () => this.signOut();
    this.hud.accountEmailCb = () => this.account.getEmail();
    this.hud.accountIsGuestCb = () => this.account.isGuest();

    // ---- Loop ----
    this.loop = new Loop(
      (dt) => this.update(dt),
      () => this.render(),
      60,
    );

    // ---- Resize / visibility ----
    window.addEventListener("resize", () => this.resize());
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", () => this.resize());
    }
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.state === "playing") this.pause();
    });
    // Every user gesture: nudge the audio context awake and retry any
    // tracks that should be playing but were blocked by autoplay rules.
    // Capture phase so we don't miss taps absorbed by intro/auth/menu
    // overlays that stopPropagation on their own click handlers.
    document.addEventListener(
      "pointerdown",
      () => {
        this.audio.unlock();
        this.audio.retryPending();
      },
      { capture: true, passive: true },
    );
    this.resize();

    // ---- iOS install hint (only if not standalone) ----
    if (this.isIos() && !this.isStandalone()) {
      this.hud.showInstallHint();
    }

    // Start render loop first so the 3D scene plays behind the intro
    this.loop.start();
    // Show intro cutscene; once done, gate on auth before showing menu.
    this.intro.show(() => this.afterIntro());
  }

  // ---- Auth gate ----

  private async afterIntro(): Promise<void> {
    const restored = await this.account.restore();
    if (restored) {
      await this.loadServerProgress();
      this.toMenu();
    } else if (this.account.isGuest()) {
      // Returning guest — skip the auth screen and play with local-only
      // progress from whatever's in localStorage.
      this.toMenu();
    } else {
      this.showAuth();
    }
  }

  private showAuth(): void {
    this.state = "auth";
    this.auth.show(
      async ({ mode, email, pin }) => {
        const r =
          mode === "signup"
            ? await this.account.signup(email, pin)
            : await this.account.login(email, pin);
        if (!r.ok) return r;
        // Server-only model: discard any pre-existing local progress and
        // adopt whatever the server has for this account.
        this.storage.clearLocal();
        await this.loadServerProgress();
        this.auth.hide();
        this.toMenu();
        return r;
      },
      () => {
        // Guest path: no account, progress stays local. Remember the
        // choice so future visits skip the auth screen.
        this.account.setGuest();
        this.applyCarSkin(carById(this.storage.get().equippedSkin));
        this.auth.hide();
        this.toMenu();
      },
    );
  }

  private async loadServerProgress(): Promise<void> {
    const server = await this.account.fetchProgress();
    if (server) {
      this.storage.replace(server);
    } else {
      // Fresh account or empty server row — start from defaults and push them
      // up so we have a record for future devices.
      this.storage.reset();
    }
    this.applyCarSkin(carById(this.storage.get().equippedSkin));
  }

  private async signOut(): Promise<void> {
    this.audio.stopMenuMusic();
    await this.account.flush(); // last save attempt
    await this.account.logout();
    this.account.clearGuest();
    this.storage.clearLocal();
    this.applyCarSkin(carById("default"));
    this.showAuth();
  }

  // ---- State transitions ----

  private toMenu(): void {
    this.state = "menu";
    this.audio.stopMusic();
    this.audio.playMenuMusic();
    this.player.reset();
    this.road.resetAll();
    this.skyline.reset();
    this.arrival.reset();
    this.spawner.resetAll();
    for (const c of this.road.chunks) this.spawner.populate(c, 0);
    this.speed = BASE_SPEED;
    this.elapsed = 0;
    this.distance = 0;
    this.runCoins = 0;
    this.runScore = 0;
    this.runPowerupCount = 0;
    this.runLaneChanges = 0;
    this.powerups.reset();
    this.resetCamera();
    const fresh = this.missions.ensureFresh();
    this.hud.showMenu(fresh, (m) => this.missions.labelFor(m));
  }

  /**
   * Put the camera back at the game's default driving POV. The arrival
   * cinematic moves the camera off to a side hero-shot at (14, 10, ...);
   * without resetting it explicitly the next run would render from that
   * angle.
   */
  private resetCamera(): void {
    this.fovBase = 70;
    this.fovTarget = 70;
    this.camera.fov = this.fovBase;
    this.camera.position.set(0, 4.2, 7);
    this.camera.lookAt(0, 1.2, -8);
    this.camera.updateProjectionMatrix();
  }

  private startRun(): void {
    this.state = "playing";
    this.player.reset();
    this.road.resetAll();
    this.skyline.reset();
    this.arrival.reset();
    this.spawner.resetAll();
    // No cars in the initial visible road — the recycle handler will start
    // adding them once the player has traveled at least 50m.
    for (const c of this.road.chunks) this.spawner.populate(c, 0, false);
    this.speed = BASE_SPEED;
    this.elapsed = 0;
    this.distance = 0;
    this.runCoins = 0;
    this.runScore = 0;
    this.runPowerupCount = 0;
    this.runLaneChanges = 0;
    this.powerups.reset();
    this.missions.resetRunScoped();
    this.resetCamera();
    this.hud.showHud();
    this.audio.sfx("swipe");
    this.audio.stopMenuMusic();
    this.audio.playMusic();
  }

  private pause(): void {
    if (this.state !== "playing") return;
    this.state = "paused";
    this.audio.pauseMusic();
    this.hud.showPause();
  }

  private resume(): void {
    if (this.state !== "paused") return;
    this.state = "playing";
    this.audio.resumeMusic();
    this.hud.hidePause();
    this.hud.showHud();
  }

  private gameOver(): void {
    if (this.state !== "playing") return;
    this.state = "gameover";
    this.audio.stopMusic();
    this.audio.sfx("crash");
    // Persist results
    let isNewBest = false;
    this.storage.update((d) => {
      if (this.runScore > d.bestScore) {
        d.bestScore = this.runScore;
        isNewBest = true;
      }
      if (this.distance > d.bestDistance) d.bestDistance = this.distance;
      d.totalCoins += this.runCoins;
      d.lifetime.runs += 1;
      d.lifetime.totalDistance += this.distance;
      d.lifetime.totalCoins += this.runCoins;
    });
    // Mission updates that finalize on run-end
    const lifeEarned   = this.missions.bump("lifetime_coins", this.runCoins);
    const laneEarned   = this.missions.setIfHigher("lane_changes", this.runLaneChanges);
    const distEarned   = this.missions.setIfHigher("distance", Math.floor(this.distance));
    const coinsEarned  = this.missions.setIfHigher("coins_in_run", this.runCoins);
    const totalReward  = lifeEarned + laneEarned + distEarned + coinsEarned;
    if (totalReward > 0) {
      this.storage.update((d) => {
        d.totalCoins += totalReward;
      });
      this.hud.showToast(`Mission reward +${totalReward}`);
    }
    this.hud.showGameOver({
      score: this.runScore,
      coins: this.runCoins,
      distance: this.distance,
      newBest: isNewBest,
    });
  }

  private levelComplete(): void {
    if (this.state !== "playing") return;
    this.state = "arrival";
    this.audio.stopMusic();

    // Persist run results immediately
    let isNewBest = false;
    this.storage.update((d) => {
      if (this.runScore > d.bestScore) {
        d.bestScore = this.runScore;
        isNewBest = true;
      }
      if (this.distance > d.bestDistance) d.bestDistance = this.distance;
      d.totalCoins += this.runCoins;
      d.lifetime.runs += 1;
      d.lifetime.totalDistance += this.distance;
      d.lifetime.totalCoins += this.runCoins;
    });

    // Mission updates
    const lifeEarned   = this.missions.bump("lifetime_coins", this.runCoins);
    const laneEarned   = this.missions.setIfHigher("lane_changes", this.runLaneChanges);
    const distEarned   = this.missions.setIfHigher("distance", Math.floor(this.distance));
    const coinsEarned  = this.missions.setIfHigher("coins_in_run", this.runCoins);
    const totalReward  = lifeEarned + laneEarned + distEarned + coinsEarned;
    if (totalReward > 0) {
      this.storage.update((d) => { d.totalCoins += totalReward; });
    }

    // Capture for use in closure
    const score   = this.runScore;
    const coins   = this.runCoins;
    const newBest = isNewBest;

    // 1) Arrival cinematic → 2) outro video → 3) victory screen
    this.arrival.play(this.camera, this.player.group, () => {
      this.state = "outro";
      this.outro.show(() => {
        this.state = "gameover";
        this.arrival.reset(); // clears fade overlay before victory screen appears
        this.hud.showVictory({ score, coins, newBest });
      });
    });
  }

  private share(): void {
    const url = location.origin;
    const listen = primaryListenLink();
    const tags = BRAND.shareHashtags.join(" ");
    const lines = [
      `I scored ${this.runScore} (${Math.floor(this.distance)}m) racing to K-town in ${BRAND.artist} 🏎️`,
      `Play: ${url}`,
    ];
    if (listen) lines.push(`Listen: ${listen}`);
    lines.push(tags);
    const text = lines.join("\n");
    const nav = navigator as Navigator & { share?: (d: any) => Promise<void> };
    if (typeof nav.share === "function") {
      nav.share({ title: BRAND.artist, text, url }).catch(() => {});
    } else {
      nav.clipboard?.writeText(text).catch(() => {});
      this.hud.showToast("Copied to clipboard");
    }
  }

  // ---- Input handlers ----

  private handleSwipe(d: SwipeDir): void {
    if (this.state === "intro" || this.state === "auth") return;
    if (this.state === "menu") {
      // First swipe also acts as "drive"
      this.startRun();
      return;
    }
    if (this.state === "paused" || this.state === "outro" || this.state === "gameover") return;
    if (this.state !== "playing") return;
    switch (d) {
      case "left":
        this.player.swipeLeft();
        this.runLaneChanges++;
        this.audio.sfx("swipe");
        break;
      case "right":
        this.player.swipeRight();
        this.runLaneChanges++;
        this.audio.sfx("swipe");
        break;
    }
  }

  private handleTap(): void {
    if (this.state === "intro" || this.state === "outro" || this.state === "auth") return;
    if (this.state === "menu") this.startRun();
  }

  // ---- Update / Render ----

  private update(dt: number): void {
    if (this.state === "playing") {
      this.elapsed += dt;
      // Speed ramp
      this.speed = Math.min(MAX_SPEED, BASE_SPEED + this.elapsed * SPEED_RAMP_PER_SEC * 4);
      const nitro = this.powerups.hasNitro() ? 1.7 : 1.0;
      const effSpeed = this.speed * nitro;

      const dz = effSpeed * dt;
      this.distance += dz;

      // Level complete
      if (this.distance >= LEVEL_END_DISTANCE) {
        this.levelComplete();
        return;
      }

      // Score: distance + coin/powerup bonuses applied on event
      const mult = this.powerups.hasX2() ? 2 : 1;
      this.runScore += Math.round(dz * 1.0 * mult);

      this.player.update(dt);
      this.road.scroll(dz);
      this.skyline.update(dz);
      this.spawner.scrollDelta(dz);
      this.powerups.tick(dt);

      // Magnet pull
      if (this.powerups.hasMagnet()) {
        this.spawner.magnetPull(this.player.group.position, 6, 8, dt);
      }

      // Collision
      this.player.hitbox(this.playerBox);
      const toRemove: any[] = [];
      this.spawner.forEachActive((s) => {
        // Quick reject: ignore items way ahead/behind
        const z = s.mesh.position.z;
        if (z < -3 || z > 3) return;
        if (intersectsSpawnable(this.playerBox, s)) {
          if (s.kind === "coin") {
            this.runCoins += 1;
            this.runScore += 5 * mult;
            this.audio.sfx("coin");
            toRemove.push(s);
          } else if (
            s.kind === "magnet" ||
            s.kind === "shield" ||
            s.kind === "nitro" ||
            s.kind === "x2"
          ) {
            this.powerups.apply(s.kind);
            this.runPowerupCount += 1;
            this.missions.bump("powerup_uses", 1);
            this.audio.sfx("powerup");
            if (s.kind === "nitro") this.fovTarget = 86;
            this.hud.showToast(this.powerupLabel(s.kind));
            toRemove.push(s);
          } else {
            // Obstacle hit
            if (this.powerups.consumeShield()) {
              this.audio.sfx("powerup");
              this.shakeT = 0.2;
              this.shakeAmp = 0.15;
              toRemove.push(s);
            } else {
              this.shakeT = 0.4;
              this.shakeAmp = 0.6;
              this.gameOver();
              return;
            }
          }
        }
      });
      for (const s of toRemove) this.spawner.despawn(s);

      // FOV interp
      if (!this.powerups.hasNitro()) this.fovTarget = this.fovBase;
      this.camera.fov += (this.fovTarget - this.camera.fov) * Math.min(1, dt * 6);
      this.camera.updateProjectionMatrix();

      // Camera shake decay
      if (this.shakeT > 0) {
        this.shakeT -= dt;
        if (this.shakeT <= 0) {
          this.shakeT = 0;
          this.shakeAmp = 0;
        }
      }

      // HUD
      this.hud.setStats(this.runScore, this.distance, this.runCoins, mult);
      this.hud.setProgress(this.distance / LEVEL_END_DISTANCE);
      this.hud.setPowerUps(this.powerups);
    } else if (this.state === "arrival") {
      // K-Town arrival cinematic — ArrivalScene drives the camera & player car
      this.arrival.update(dt);
    } else if (this.state === "menu" || this.state === "intro" || this.state === "auth") {
      // Slow ambient scroll behind the cutscene/menu/auth screen so the world stays alive.
      const dz = 6 * dt;
      this.road.scroll(dz);
      this.skyline.update(dz);
      this.spawner.scrollDelta(dz);
      this.player.update(dt);
    }
  }

  private render(): void {
    // During the arrival cinematic ArrivalScene owns the camera — don't override it.
    if (this.state !== "arrival") {
      if (this.shakeT > 0) {
        const a = this.shakeAmp * (this.shakeT / 0.4);
        this.camera.position.x = (Math.random() - 0.5) * a;
        this.camera.position.y = 4.2 + (Math.random() - 0.5) * a * 0.5;
      } else {
        this.camera.position.x = 0;
        this.camera.position.y = 4.2;
      }
    }
    this.renderer.render(this.scene, this.camera);
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ---- Helpers ----

  private difficulty(): number {
    // 0..1 over the course of a run, capped
    return Math.min(1, this.elapsed / 90);
  }

  private applyCarSkin(car: CarDef): void {
    const body = this.player.body;
    const mat = body.material as THREE.MeshStandardMaterial;
    mat.color.setHex(car.bodyColor);
    if (car.emissive) {
      mat.emissive.setHex(car.emissive);
      mat.emissiveIntensity = 0.25;
    } else {
      mat.emissive.setHex(0x000000);
      mat.emissiveIntensity = 0;
    }
  }

  private powerupLabel(k: string): string {
    switch (k) {
      case "magnet": return "🧲 Magnet";
      case "shield": return "🛡 Shield";
      case "nitro": return "🔥 Nitro";
      case "x2": return "×2 Score";
      default: return k;
    }
  }

  private isIos(): boolean {
    const ua = navigator.userAgent;
    return /iP(hone|od|ad)/.test(ua) || (/Mac/.test(ua) && "ontouchend" in document);
  }
  private isStandalone(): boolean {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as any).standalone === true
    );
  }

  // Unused but kept for completeness
  // (CARS used in HUD, suppress unused-warning by referencing here)
  static readonly _carsRef = CARS;
  static readonly _chunkLenRef = CHUNK_LENGTH;
}
